import { SECTION_ORDER, monthName, type Section, type SectionKey } from './listSections'
import type { YearMonth } from './monthParam'
import type { Entry } from './recurringWire'

/**
 * The calendar's pure parts: the month's shape, which entries fall on which day,
 * which status a day's badge shows, what a cell is called, what a narrowing
 * says, and where an arrow key goes. Kept out of the component so each is
 * pinned by a test of its own.
 */

/** `firstWeekday` is 0 for Sunday (the week starts on Sunday, as on iOS). */
export type MonthShape = { firstWeekday: number; dayCount: number; weeks: number }

/** UTC arithmetic on the month's own numbers, so no zone can move a day. */
export function monthGrid(ym: YearMonth): MonthShape {
  const firstWeekday = new Date(Date.UTC(ym.year, ym.month - 1, 1)).getUTCDay()
  const dayCount = new Date(Date.UTC(ym.year, ym.month, 0)).getUTCDate()
  return { firstWeekday, dayCount, weeks: Math.ceil((firstWeekday + dayCount) / 7) }
}

export type DayCounts = Record<SectionKey | 'bills' | 'income', number>

/** One entry on a day, with the list's own section for it. */
export type DayEntry = { entry: Entry; key: SectionKey }

export type Day = { entries: DayEntry[]; counts: DayCounts }

const zeroCounts = (): DayCounts => ({ bills: 0, overdue: 0, upcoming: 0, skipped: 0, paid: 0, income: 0 })

/**
 * Each day of the month that has entries, keyed by day number.
 *
 * **Built from `monthSections`, never from the server's status** (Ruling 30):
 * the list's sections are the one classifier, so a day the grid calls overdue
 * is overdue in the list, the tile and the badge too. Walking the sections in
 * `SECTION_ORDER` also puts each day's entries in urgency order — overdue,
 * upcoming, skipped, paid — so the first outflow is the day's badge status.
 *
 * One entry per OCCURRENCE (S5): a weekly bill lands on four or five days.
 * An inflow is bucketed but counted as income, never as a bill (D6).
 */
export function dayBuckets(sections: Record<SectionKey, Section>): Map<number, Day> {
  const days = new Map<number, Day>()
  for (const key of SECTION_ORDER) {
    for (const entry of sections[key].entries) {
      const n = Number(entry.dueDate.slice(8, 10))
      let d = days.get(n)
      if (!d) {
        d = { entries: [], counts: zeroCounts() }
        days.set(n, d)
      }
      d.entries.push({ entry, key })
      if (entry.bill.isInflow) d.counts.income += 1
      else {
        d.counts.bills += 1
        d.counts[key] += 1
      }
    }
  }
  return days
}

/**
 * The status a day's ONE badge shows: its most urgent OUTFLOW's section, which
 * is the first outflow because `dayBuckets` walks `SECTION_ORDER`. Null for a
 * day with only inflows: the badge counts bills, and an inflow is none (D6).
 * Read from the list's own sections, never the server, so it cannot disagree
 * with the row it stands for (Ruling 30).
 */
export function dayStatus(day: Day): SectionKey | null {
  return day.entries.find((e) => !e.entry.bill.isInflow)?.key ?? null
}

/** The badge's count: always shown, one included; 9+ past nine (the cell's name keeps the true number). */
export function badgeText(bills: number): string {
  return bills > 9 ? '9+' : String(bills)
}

type NarrowCounts = { bills: number; income: number }

const billWord = (n: number) => `${n} ${n === 1 ? 'bill' : 'bills'}`

/** The narrowed scope bar's right side: "2 bills", "1 bill · 1 income", "1 income". */
export function scopeCountLabel({ bills, income }: NarrowCounts): string {
  if (bills === 0) return `${income} income`
  return income === 0 ? billWord(bills) : `${billWord(bills)} · ${income} income`
}

/** What a narrowing says aloud: counts and the day only, never a name or an amount (rule 7's spirit). */
export function narrowedAnnouncement({ bills, income }: NarrowCounts, day: string): string {
  if (bills === 0 && income === 0) return `Nothing due ${day}`
  if (bills === 0) return `Showing ${income} income due ${day}`
  return income === 0 ? `Showing ${billWord(bills)} due ${day}` : `Showing ${billWord(bills)} and ${income} income due ${day}`
}

/** The word a cell's name uses for each bucket. "due", not "upcoming": it is what the ring means. */
const SPOKEN: Record<SectionKey, string> = { overdue: 'overdue', upcoming: 'due', skipped: 'skipped', paid: 'paid' }

/**
 * "July 22, 2 bills, 1 overdue, 1 paid": counts, never amounts or names. Zero
 * buckets are left out. Income follows the bills, so an occupied day is never
 * unexplained. Selection is `aria-selected` on the cell, not a word here.
 */
export function cellLabel(ym: YearMonth, day: number, counts: DayCounts, isToday: boolean): string {
  const parts: string[] = []
  if (isToday) parts.push('Today')
  parts.push(`${monthName(ym)} ${day}`)
  parts.push(counts.bills === 0 ? 'no bills' : `${counts.bills} ${counts.bills === 1 ? 'bill' : 'bills'}`)
  for (const key of SECTION_ORDER) if (counts[key] > 0) parts.push(`${counts[key]} ${SPOKEN[key]}`)
  if (counts.income > 0) parts.push(`${counts.income} income`)
  return parts.join(', ')
}

/**
 * Where a key moves focus from `day`, or null when the key is not the grid's.
 *
 * The APG date-grid keys, addressed by DAY NUMBER rather than by row and
 * column, which is why `useRovingGrid` is not used (Ruling 16): its fixed
 * positions clamp per row and cannot wrap, and a leading blank cell would hold
 * its focus. Left/Right step a day and wrap across weeks; Up/Down step a week;
 * Home/End reach the week's ends. Every move clamps to the month — a step that
 * would leave it stays put.
 */
export function moveDay(key: string, day: number, shape: Pick<MonthShape, 'firstWeekday' | 'dayCount'>): number | null {
  const weekday = (shape.firstWeekday + day - 1) % 7
  const target =
    key === 'ArrowRight'
      ? day + 1
      : key === 'ArrowLeft'
        ? day - 1
        : key === 'ArrowDown'
          ? day + 7
          : key === 'ArrowUp'
            ? day - 7
            : key === 'Home'
              ? Math.max(day - weekday, 1)
              : key === 'End'
                ? Math.min(day + (6 - weekday), shape.dayCount)
                : null
  if (target === null) return null
  return target < 1 || target > shape.dayCount ? day : target
}
